import React from "react";
import { DashboardHeroStudy } from "./DashboardHeroStudy";
import { DashboardHeroMastery } from "./DashboardHeroMastery";
import { useTranslation } from "@/context/LanguageContext";

export type DashboardTab = "study" | "mastery";

interface DashboardHeroProps {
  activeTab: DashboardTab;
  onTabChange: (tab: DashboardTab) => void;
}

export const DashboardHero: React.FC<DashboardHeroProps> = ({
  activeTab,
  onTabChange
}) => {
  const { t } = useTranslation();
  const isStudy = activeTab === "study";

  return (
    <section className="mt-12 mb-12 flex w-full flex-col items-center sm:mb-24 sm:mt-20 sm:px-10 lg:px-24">
      <div className="flex w-full flex-col items-center">
        <div
          role="tablist"
          aria-orientation="horizontal"
          className="mb-2.5 inline-flex h-auto items-center gap-1 rounded-full border border-border bg-secondary/50 p-1 text-muted-foreground shadow-sm"
        >
          <button
            type="button"
            role="tab"
            aria-selected={isStudy}
            onClick={() => onTabChange("study")}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 ${
              isStudy
                ? "bg-blue-accent/10 text-blue-accent shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-book h-4 w-4"
              aria-hidden="true"
            >
              <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H19a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1H6.5a1 1 0 0 1 0-5H20" />
            </svg>
            {t("dashboard.study")}
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={!isStudy}
            onClick={() => onTabChange("mastery")}
            className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 ${
              !isStudy
                ? "bg-purple-accent/10 text-purple-accent shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <svg
              stroke="currentColor"
              fill="currentColor"
              strokeWidth="0"
              viewBox="0 0 256 256"
              className="h-4 w-4"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path d="M200,164a36.07,36.07,0,0,0-33.94,24H72a28,28,0,0,1,0-56h96a44,44,0,0,0,0-88H72a12,12,0,0,0,0,24h96a20,20,0,0,1,0,40H72a52,52,0,0,0,0,104h94.06A36,36,0,1,0,200,164Zm0,48a12,12,0,1,1,12-12A12,12,0,0,1,200,212Z" />
            </svg>
            {t("dashboard.mastery")}
          </button>
        </div>

        <div className="mt-2 flex w-full flex-col items-center gap-[13px] sm:mt-3 sm:gap-3 bg-transparent">
          {isStudy ? <DashboardHeroStudy /> : <DashboardHeroMastery />}
        </div>
      </div>
    </section>
  );
};
